import { useEffect, useRef, useState } from 'react';
import { Eye, EyeOff, MoreHorizontal, Pencil, Trash2, Unlink } from 'lucide-react';

export default function TextBoxActionsMenu({ name, hidden, disabled, onRename, onToggle, onRemove, onDelete }) {
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const close = () => { setOpen(false); setConfirmDelete(false); setError(''); triggerRef.current?.focus(); };
  useEffect(() => {
    if (!open) return undefined;
    rootRef.current?.querySelector('[role="menu"] button')?.focus();
    const outside = (event) => {
      if (!busy && !rootRef.current?.contains(event.target)) { setOpen(false); setConfirmDelete(false); setError(''); }
    };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [open, busy, confirmDelete]);
  const run = async (action) => {
    if (busy || disabled) return;
    setBusy(true); setError('');
    try {
      if (await action() === false) setError('Could not finish this action. Please try again.');
      else close();
    } catch { setError('Could not finish this action. Please try again.'); }
    finally { setBusy(false); }
  };
  const actionClass = 'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-xs font-medium hover:bg-slate-100 focus:bg-slate-100 focus:outline-none disabled:opacity-40';
  return <div ref={rootRef} className="relative shrink-0" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => {
    event.stopPropagation();
    if (event.key === 'Escape' && !busy) { event.preventDefault(); close(); }
    if (open && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const buttons = [...rootRef.current.querySelectorAll('[role="menu"] button:not(:disabled)')];
      const index = buttons.indexOf(document.activeElement);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
      buttons[next]?.focus();
    }
    if (event.key === 'Tab' && !busy) { setOpen(false); setConfirmDelete(false); }
  }}>
    <button ref={triggerRef} type="button" disabled={disabled || busy} aria-label={`Actions for ${name}`} aria-haspopup="menu" aria-expanded={open} onClick={() => open ? close() : setOpen(true)} className="flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-white hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-40"><MoreHorizontal size={18} /></button>
    {open && <div role="menu" aria-label={`Actions for ${name}`} className="absolute right-0 top-8 z-50 w-56 rounded-xl border border-slate-200 bg-white p-1.5 text-slate-700 shadow-xl">
      {confirmDelete ? <>
        <p className="px-3 py-2 text-xs text-slate-600">Permanently delete “{name}”, its wording, translations, and layout boxes? This cannot be undone.</p>
        <button role="menuitem" type="button" disabled={busy} onClick={() => setConfirmDelete(false)} className={actionClass}>Cancel</button>
        <button role="menuitem" type="button" disabled={busy} onClick={() => run(onDelete)} className={`${actionClass} text-red-700`}><Trash2 size={14} />{busy ? 'Deleting…' : 'Delete permanently'}</button>
      </> : <>
        {onRename && <button role="menuitem" type="button" disabled={busy} onClick={() => { close(); onRename(); }} className={actionClass}><Pencil size={14} />Rename</button>}
        <button role="menuitem" type="button" disabled={busy} onClick={() => run(onToggle)} className={actionClass}>{hidden ? <Eye size={14} /> : <EyeOff size={14} />}{hidden ? 'Show on canvas' : 'Hide from canvas'}</button>
        <button role="menuitem" type="button" disabled={busy} onClick={() => run(onRemove)} className={actionClass}><Unlink size={14} />Remove from layout</button>
        {onDelete && <div className="mt-1 border-t border-slate-100 pt-1"><button role="menuitem" type="button" disabled={busy} onClick={() => setConfirmDelete(true)} className={`${actionClass} text-red-700`}><Trash2 size={14} />Delete text…</button></div>}
      </>}
      {error && <p role="alert" className="px-3 py-2 text-xs text-red-700">{error}</p>}
    </div>}
  </div>;
}
